// src/components/costs/ZahlungsanweisungModal.jsx
// Modal "Zahlungsanweisung erstellen / bearbeiten" einer BKP-Position
// (US-KOST-022 + US-KOST-023, ADR 0054 + ADR 0064).
//
// Automatisch vorbefuellt: Vertrag (Vertragsgruppe der Position; bei mehreren
// Vertraegen Pflichtwahl), Unternehmer (aus dem Vertrag bzw. CRM), Projekt,
// Auftraggeberin (CRM-Relation "auftraggeberin" des verknuepften Projekts),
// Auftrag (BKP + Bezeichnung), Vertrag Nr., Vertragssumme (Vertrag + seine
// Nachtraege), bisherige Zahlungen (Zahlungen derselben Vertragsgruppe),
// Objektgliederung (Phase, Sidebar), Datum (heute), naechste Nummer
// (je Dossier und Phase ueber alle Positionen). Alles Weitere wird hier erfasst.
//
// Erstellen: erst Zahlung speichern (inkl. Snapshot `zaDaten`), dann PDF.
// Bearbeiten (`existingPayment`): alle Angaben aus dem Snapshot editierbar;
// Vertragssumme/bisherige Zahlungen bleiben auf dem Stand des Originals, bis
// sie bewusst neu berechnet werden oder ein anderer Vertrag gewaehlt wird.
// Speichern aktualisiert die Zahlung; optional wird das PDF neu erzeugt.
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getProjectRelations,
  createRelation,
  deleteRelation,
  RELATION_TYPES,
} from "../../services/azureRelationsService";
import { formatSwissCurrency } from "../../services/costs/controlCalculations.js";
import {
  downloadZahlungsanweisungPdf,
  berechneMwst,
  zahlungsanweisungDateiname,
} from "../../services/costs/zahlungsanweisungPdf.js";
import { getAllPersons } from "../../services/azurePersonsService";
import { getPaymentsByEstimate } from "../../services/azureCostEstimatePaymentsService.js";
import {
  buildContractGroups,
  resolveGroup,
  groupUnternehmer,
  groupVertragNr,
  groupLabel,
} from "../../services/costs/contractGroups.js";
import UnternehmerPicker, { partnerAdresse, personAdresse, personName } from "./UnternehmerPicker.jsx";

const DEFAULTS_KEY = "costControlZa:defaults";
const estimateKey = (estimateId) => `costControlZa:${estimateId}`;

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* localStorage optional */
  }
};

const sum = (list, pick = (x) => x.betrag) => list.reduce((s, x) => s + (Number(pick(x)) || 0), 0);
const round2 = (v) => Math.round((Number(v) + Number.EPSILON) * 100) / 100;
const distinctNrs = (list) =>
  Array.from(new Set(list.map((c) => String(c.vertragNr || "").trim()).filter(Boolean))).join(", ");

const ZahlungsanweisungModal = ({
  code,
  nodeName = "",
  contracts = [],
  payments = [], // Zahlungen der Position
  allPayments = [], // alle Zahlungen der Phase (Nummerierung je Dossier und Phase)
  phase = "",
  existingPayment = null, // Zahlung mit zaDaten -> Bearbeiten-Modus
  objektgliederung = "", // aktuelle Phase (Sidebar)
  estimateId,
  estimateClient = "",
  linkedProject = null, // {code, name, auftraggeberin, adresse}
  currentUserName = "",
  partners = [],
  onPartnersReload,
  onSavePayment, // async (code, payload)
  onUpdatePayment, // async (code, id, payload)
  onUpdateContract, // async (code, id, payload)
  onClose,
}) => {
  const isEdit = !!existingPayment;
  const zd = existingPayment?.zaDaten || null;
  const og = (objektgliederung || "").trim();

  const storedDefaults = useMemo(() => readJson(DEFAULTS_KEY) || {}, []);
  const storedEstimate = useMemo(() => readJson(estimateKey(estimateId)) || {}, [estimateId]);

  // --- Vertragsgruppen der Position (US-KOST-023) --------------------------------
  // Ein Vertrag mit seinen Nachtraegen; die Zahlungsanweisung bezieht sich auf
  // genau eine Gruppe. Bei mehreren Vertraegen ist die Wahl Pflicht — ein
  // stiller Default wuerde Vertragssumme und Nummer der falschen Vergabe zeigen.
  const { groups, unassigned: unassignedNachtraege } = useMemo(() => buildContractGroups(contracts), [contracts]);
  const mustChooseVertrag = groups.length > 1;
  const initialVertragId = (() => {
    if (isEdit) return resolveGroup(existingPayment, groups)?.id || "";
    return groups.length === 1 ? groups[0].id : "";
  })();
  const [vertragId, setVertragId] = useState(initialVertragId);
  const group = useMemo(() => groups.find((g) => g.id === vertragId) || null, [groups, vertragId]);

  // Bearbeiten: Vertragssumme/bisherige Zahlungen aus dem Original-Snapshot,
  // bis bewusst neu berechnet oder ein anderer Vertrag gewaehlt wird.
  const [frozen, setFrozen] = useState(() =>
    isEdit && zd
      ? {
          vertragssumme: round2(zd.vertragssumme),
          bisherige: round2(zd.bisherigeZahlungen),
          bisherigeAkonto: round2(zd.bisherigeAkonto),
        }
      : null
  );

  // --- Unternehmer ---------------------------------------------------------------
  const unternehmerOptions = useMemo(() => {
    const seen = new Map();
    for (const c of contracts) {
      if (c.unternehmerId && !seen.has(c.unternehmerId)) {
        seen.set(c.unternehmerId, { id: c.unternehmerId, name: c.unternehmerName || "" });
      }
    }
    return Array.from(seen.values());
  }, [contracts]);

  const addressFor = (u) => {
    if (!u) return "";
    const partner = partners.find((p) => p.id === u.id);
    return partner ? partnerAdresse(partner) : u.name || "";
  };

  const [unternehmer, setUnternehmer] = useState(() => {
    if (isEdit && zd?.unternehmerId) return { id: zd.unternehmerId, name: zd.unternehmerName || "" };
    const g = groups.find((x) => x.id === initialVertragId) || null;
    // Bei mehreren Vertraegen gilt nur die Firma des gewaehlten Vertrags —
    // nie die eines anderen Vertrags der Position.
    return groupUnternehmer(g) || (groups.length <= 1 ? unternehmerOptions[0] || null : null);
  });
  const [unternehmerText, setUnternehmerText] = useState(() => (isEdit ? zd?.unternehmer || "" : ""));
  const [saveOnContracts, setSaveOnContracts] = useState(true);
  // Zuletzt automatisch gesetzter Text; null = Snapshot-Text (Bearbeiten), der
  // nie automatisch ueberschrieben wird. Handeingaben bleiben ebenfalls stehen.
  const unternehmerAutoRef = useRef(isEdit ? null : "");

  useEffect(() => {
    const auto = addressFor(unternehmer);
    setUnternehmerText((prev) => {
      if (unternehmerAutoRef.current === null) return prev;
      return !prev || prev === unternehmerAutoRef.current ? auto : prev;
    });
    if (unternehmerAutoRef.current !== null) unternehmerAutoRef.current = auto;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unternehmer, partners]);

  // Explizite Wahl (Picker, Vertragswechsel): Adresse immer uebernehmen.
  const chooseUnternehmer = (u) => {
    setUnternehmer(u);
    const text = addressFor(u);
    setUnternehmerText(text);
    unternehmerAutoRef.current = text;
  };

  // --- Relevante Vertraege/Zahlungen -----------------------------------------------
  // Mit Vertragsgruppen: genau die Gruppe. Ohne Vertrag (nur Nachtraege/Altdaten):
  // Filter je Unternehmer wie bisher (Altdaten ohne Unternehmer zaehlen mit).
  const legacyFilter = (list) =>
    list.filter((x) => !x.unternehmerId || !unternehmer?.id || x.unternehmerId === unternehmer.id);

  const relevantContracts = useMemo(() => {
    if (groups.length) return group ? group.contracts : [];
    return legacyFilter(contracts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, group, contracts, unternehmer]);

  // Beim Bearbeiten zaehlen als "bisherige Zahlungen" nur Zahlungen VOR dieser
  // Anweisung (kleinere ZA-Nr., sonst frueheres/gleiches Datum) — spaeter
  // gebuchte gehoeren nicht in dieses Dokument.
  const precedesExisting = (p) => {
    if (!isEdit) return true;
    const a = parseInt(p.zaNr, 10);
    const b = parseInt(existingPayment.zaNr, 10);
    if (Number.isFinite(a) && Number.isFinite(b)) return a < b;
    return (p.datum || "") <= (existingPayment.datum || "");
  };
  const otherPayments = useMemo(
    () => payments.filter((p) => (!isEdit || p.id !== existingPayment.id) && precedesExisting(p)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payments, isEdit, existingPayment]
  );
  const relevantPayments = useMemo(() => {
    if (!groups.length) return legacyFilter(otherPayments);
    if (!group) return [];
    return otherPayments.filter((p) => resolveGroup(p, groups)?.id === group.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, group, otherPayments, unternehmer]);
  const unassignedPayments = useMemo(
    () => (mustChooseVertrag ? otherPayments.filter((p) => !resolveGroup(p, groups)) : []),
    [mustChooseVertrag, otherPayments, groups]
  );

  const vertragssummeCalc = useMemo(() => round2(sum(relevantContracts)), [relevantContracts]);
  const bisherigeCalc = useMemo(() => round2(sum(relevantPayments)), [relevantPayments]);
  const bisherigeAkontoCalc = useMemo(
    () => round2(sum(relevantPayments.filter((p) => p.istAkonto))),
    [relevantPayments]
  );
  const vertragssumme = frozen ? frozen.vertragssumme : vertragssummeCalc;
  const bisherige = frozen ? frozen.bisherige : bisherigeCalc;
  const bisherigeAkonto = frozen ? frozen.bisherigeAkonto : bisherigeAkontoCalc;
  const frozenDiffers =
    !!frozen && (frozen.vertragssumme !== vertragssummeCalc || frozen.bisherige !== bisherigeCalc);

  const vertragNrVorschlag = useMemo(() => {
    if (group) return groupVertragNr(group);
    const sorted = [...relevantContracts].sort((a, b) => (a.istNachtrag ? 1 : 0) - (b.istNachtrag ? 1 : 0));
    return distinctNrs(sorted);
  }, [group, relevantContracts]);

  // --- Naechste Nummer: je Dossier und Phase (alle Positionen der Phase) ------------
  // Entscheid 2026-09-24: jede Phase zaehlt ab 1 (BETA kennt nur noch ein Dossier
  // je Phase). Die Phase wird frisch nachgeladen, damit Anweisungen, die andere
  // Personen seit dem Oeffnen des Editors erfasst haben, mitzaehlen.
  const [phasePayments, setPhasePayments] = useState([]);
  useEffect(() => {
    if (!phase) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = await getPaymentsByEstimate(estimateId, phase);
        if (!cancelled) setPhasePayments(list);
      } catch {
        /* Vorschlag stuetzt sich dann auf die im Editor geladenen Zahlungen */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estimateId, phase]);

  const zaNrPool = useMemo(() => {
    const byId = new Map();
    for (const p of [...allPayments, ...payments, ...phasePayments]) {
      if (isEdit && p.id === existingPayment.id) continue;
      const n = parseInt(p.zaNr, 10);
      if (Number.isFinite(n)) byId.set(p.id, n);
    }
    return Array.from(byId.values());
  }, [allPayments, payments, phasePayments, isEdit, existingPayment]);
  const naechsteZaNr = useMemo(() => String(zaNrPool.reduce((m, n) => (n > m ? n : m), 0) + 1), [zaNrPool]);
  const zaNrVergeben = (nr) => {
    const n = parseInt(nr, 10);
    return Number.isFinite(n) && zaNrPool.includes(n);
  };

  // --- Formular ----------------------------------------------------------------
  const [form, setForm] = useState(() =>
    isEdit && zd
      ? {
          ort: zd.ort || storedDefaults.ort || "Basel",
          datum: zd.datum || existingPayment.datum || todayIso(),
          zaNr: zd.zaNr || existingPayment.zaNr || "",
          kuerzel: zd.kuerzel || "",
          projekt: zd.projekt || "",
          auftraggeberin: zd.auftraggeberin || "",
          adressat: zd.adressat || "",
          auftrag: zd.auftrag || "",
          vertragNr: zd.vertragNr || "",
          rechnungNr: zd.rechnungNr || existingPayment.rechnungNr || "",
          buchungstext: zd.buchungstext || "",
          rechnungsdatum: zd.rechnungsdatum || "",
          faelligkeit: zd.faelligkeit || "",
          istAkonto: zd.istAkonto ? "ja" : "nein",
          betrag: zd.betrag != null ? String(zd.betrag) : String(existingPayment.betrag ?? ""),
          mwstSatz: zd.mwstSatz != null ? String(zd.mwstSatz) : "8.1",
          unterschrift1Name: zd.unterschrift1?.name || "",
          unterschrift1Funktion: zd.unterschrift1?.funktion || "",
          unterschrift2Name: zd.unterschrift2?.name || "",
          unterschrift2Funktion: zd.unterschrift2?.funktion || "",
        }
      : {
          ort: storedDefaults.ort || "Basel",
          datum: todayIso(),
          zaNr: "",
          kuerzel: storedEstimate.kuerzel || "",
          projekt: linkedProject ? `${linkedProject.code} ${linkedProject.name || ""}`.trim() : "",
          auftraggeberin: "",
          adressat: storedEstimate.adressat || "",
          auftrag: `${code} ${nodeName || ""}`.trim(),
          vertragNr: "",
          rechnungNr: "",
          buchungstext: "",
          rechnungsdatum: "",
          faelligkeit: storedDefaults.faelligkeit || "30 Tage, netto",
          istAkonto: "ja",
          betrag: "",
          mwstSatz: storedDefaults.mwstSatz != null ? String(storedDefaults.mwstSatz) : "8.1",
          unterschrift1Name: storedDefaults.unterschrift1?.name || currentUserName || "",
          unterschrift1Funktion: storedDefaults.unterschrift1?.funktion || "",
          unterschrift2Name: storedDefaults.unterschrift2?.name || "",
          unterschrift2Funktion: storedDefaults.unterschrift2?.funktion || "",
        }
  );
  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  // Vorschlaege fuer Nr. und Vertrag Nr. nachfuehren, solange nicht von Hand
  // ueberschrieben (Feld leer oder noch gleich dem letzten Vorschlag). Beim
  // Bearbeiten bleiben die Werte des Snapshots.
  const [lastSuggest, setLastSuggest] = useState({ zaNr: "", vertragNr: "" });
  useEffect(() => {
    if (!isEdit) {
      setForm((p) => ({
        ...p,
        zaNr: !p.zaNr || p.zaNr === lastSuggest.zaNr ? naechsteZaNr : p.zaNr,
        vertragNr: !p.vertragNr || p.vertragNr === lastSuggest.vertragNr ? vertragNrVorschlag : p.vertragNr,
      }));
    }
    setLastSuggest({ zaNr: naechsteZaNr, vertragNr: vertragNrVorschlag });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [naechsteZaNr, vertragNrVorschlag]);

  // Vertrag wechseln: Unternehmer und Vertrag Nr. folgen, eingefrorene Werte
  // des Originals gelten fuer den neuen Vertrag nicht mehr.
  const chooseVertrag = (id) => {
    setVertragId(id);
    setFrozen(null);
    const g = groups.find((x) => x.id === id) || null;
    // Unternehmer des Vertrags uebernehmen; hat er keinen, Feld leeren statt
    // die Firma des vorher gewaehlten Vertrags stehen zu lassen.
    chooseUnternehmer(groupUnternehmer(g));
    const nr = groupVertragNr(g);
    setForm((p) => ({ ...p, vertragNr: nr }));
    setLastSuggest((s) => ({ ...s, vertragNr: nr }));
  };

  // --- Auftraggeberin aus den Projekt-Relationen (CRM) --------------------------
  // Quelle 1: Relation project -> partner mit Rolle AUFTRAGGEBERIN (ADR 0024),
  // Adresse aus dem CRM. Quelle 2 (Fallback): Freitext `auftraggeberin` des
  // Projekts bzw. `client` des Dossiers — nur ein String, ohne Adresse.
  // Die Person kann die Auftraggeberin hier aus dem CRM waehlen oder dort neu
  // erfassen; auf Wunsch wird sie als Projekt-Relation gespeichert und ersetzt
  // damit bisherige Relationen bzw. den reinen Text (Relation gewinnt ueberall).
  // Der automatisch gesetzte Text wird gemerkt: Laedt das CRM nach (partners
  // zunaechst leer), ersetzt die Adresse den Fallback nur, solange die Person
  // das Feld nicht von Hand geaendert hat. Beim Bearbeiten steht der
  // Snapshot-Text im Feld und bleibt dadurch unangetastet.
  const [agLoading, setAgLoading] = useState(!!linkedProject?.code);
  const [agPartner, setAgPartner] = useState(null); // {id, name} aus CRM
  const [agRelations, setAgRelations] = useState([]); // bestehende AUFTRAGGEBERIN-Relationen
  const [agPersist, setAgPersist] = useState(true);
  const [agStringOnly, setAgStringOnly] = useState(false);
  const agAutoRef = useRef("");

  const filterAgRelations = (rels) =>
    rels.filter(
      (r) =>
        r.sourceType === "project" &&
        r.targetType === "partner" &&
        r.relationType === RELATION_TYPES.AUFTRAGGEBERIN
    );

  const applyAgText = (text) => {
    const prevAuto = agAutoRef.current;
    agAutoRef.current = text;
    setForm((p) => ({
      ...p,
      auftraggeberin: !p.auftraggeberin || p.auftraggeberin === prevAuto ? text : p.auftraggeberin,
      adressat: !p.adressat || p.adressat === prevAuto ? text : p.adressat,
    }));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let text = "";
      let partner = null;
      if (linkedProject?.code) {
        try {
          const rels = filterAgRelations(await getProjectRelations(linkedProject.code));
          if (cancelled) return;
          setAgRelations(rels);
          partner = rels.length ? partners.find((p) => p.id === rels[0].targetId) : null;
          if (partner) text = partnerAdresse(partner);
        } catch {
          /* Fallback unten */
        }
      }
      const fallback = linkedProject?.auftraggeberin || estimateClient || "";
      if (!text) text = fallback;
      if (cancelled) return;
      setAgStringOnly(!partner && !!fallback);
      if (partner) setAgPartner((prev) => prev || { id: partner.id, name: partner.name });
      applyAgText(text);
      setAgLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // partners kann nach dem Oeffnen noch leer sein — dann erneut versuchen
  }, [linkedProject?.code, partners.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auswahl/Neuerfassung im Picker: Adresse uebernehmen; nach Neuerfassung hat
  // das CRM-Modal die AUFTRAGGEBERIN-Relation bereits angelegt -> nachladen.
  const handleAgPick = async (partner, meta = {}) => {
    if (!partner) {
      setAgPartner(null);
      return;
    }
    setAgPartner({ id: partner.id, name: partner.name });
    // Explizite Wahl ersetzt auch einen Snapshot-/Handtext der Auftraggeberin.
    // Der Adressat folgt nur, solange er die Auftraggeberin spiegelt (kein
    // eigener CRM-Eintrag gewaehlt, Text leer oder gleich Auftraggeberin/Auto-Text).
    const text = partnerAdresse(partner);
    const prevAuto = agAutoRef.current;
    agAutoRef.current = text;
    setForm((p) => {
      const adressatFolgt =
        !adressatEntity && (!p.adressat || p.adressat === prevAuto || p.adressat === p.auftraggeberin);
      return { ...p, auftraggeberin: text, adressat: adressatFolgt ? text : p.adressat };
    });
    if (meta.created && linkedProject?.code) {
      try {
        setAgRelations(filterAgRelations(await getProjectRelations(linkedProject.code)));
      } catch {
        /* Checkbox zeigt dann ggf. "hinterlegen" statt "ersetzen" */
      }
    }
  };

  // --- Adressat (Briefkopf links) -----------------------------------------------
  // Default = Auftraggeberin (applyAgText). Zusaetzlich aus dem CRM waehlbar:
  // Firma ODER Person (Ansprechperson). Personen werden hier lazy geladen.
  const [persons, setPersons] = useState([]);
  const [adressatEntity, setAdressatEntity] = useState(null); // {id, name, kind}
  const reloadPersons = async () => {
    try {
      setPersons(await getAllPersons());
    } catch {
      /* Personen-Suche bleibt dann leer */
    }
  };
  useEffect(() => {
    reloadPersons();
  }, []);

  const handleAdressatPick = (entity, meta = {}) => {
    if (!entity) {
      setAdressatEntity(null);
      return;
    }
    const isPerson = meta.kind === "person";
    setAdressatEntity({ id: entity.id, name: isPerson ? personName(entity) : entity.name, kind: meta.kind });
    // Explizite Wahl: Text direkt setzen (nicht ueber die Auto-Logik der
    // Auftraggeberin, damit spaetere Auftraggeberin-Aenderungen ihn nicht ersetzen).
    set("adressat", isPerson ? personAdresse(entity, partners) : partnerAdresse(entity));
  };

  const agAlreadyLinked = !!agPartner && agRelations.some((r) => r.targetId === agPartner.id);
  const agOtherRels = agPartner ? agRelations.filter((r) => r.targetId !== agPartner.id) : [];
  const agOtherNames = agOtherRels.map((r) => partners.find((p) => p.id === r.targetId)?.name || r.targetId);
  const agNeedsPersist = !!agPartner && !!linkedProject?.code && (!agAlreadyLinked || agOtherRels.length > 0);

  // Auftraggeberin als Projekt-Relation speichern (ersetzt bisherige Relationen).
  // Fehler blockieren die Zahlungsanweisung nicht — nur Warnung.
  const persistAuftraggeberin = async () => {
    if (!agNeedsPersist || !agPersist) return;
    try {
      if (!agAlreadyLinked) {
        await createRelation({
          sourceType: "project",
          sourceId: linkedProject.code,
          targetType: "partner",
          targetId: agPartner.id,
          relationType: RELATION_TYPES.AUFTRAGGEBERIN,
          function: "",
          description: "Auftraggeberin (über Zahlungsanweisung)",
          visibility: true,
        });
      }
      for (const r of agOtherRels) await deleteRelation(r.id);
      setAgRelations(filterAgRelations(await getProjectRelations(linkedProject.code)));
      setAgStringOnly(false);
    } catch (err) {
      setWarn(`Auftraggeberin konnte nicht im Projekt gespeichert werden: ${err?.message || "Unbekannter Fehler"}`);
    }
  };

  // --- Ableitungen fuer die Vorschau ---------------------------------------------
  const betrag = round2(parseFloat(form.betrag) || 0);
  const mwst = berechneMwst(betrag, parseFloat(form.mwstSatz) || 0);
  const totalNachher = round2(bisherige + betrag);
  const pctOf = (v) => (vertragssumme > 0 ? `${Math.round((v / vertragssumme) * 100)} %` : "–");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [warn, setWarn] = useState("");
  // Nach Erfolg: { zaDaten, saved, updated, pdf } — saved=false bei "Nur PDF
  // erzeugen" (Zahlung kann aus dem Abschluss-Schritt noch eingetragen werden).
  const [done, setDone] = useState(null);

  const buildZaDaten = () => {
    const now = new Date().toISOString();
    const unternehmerLines = unternehmerText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    return {
      version: 2,
      erstelltAm: zd?.erstelltAm || now,
      geaendertAm: isEdit ? now : undefined,
      projektCode: linkedProject?.code || zd?.projektCode || "",
      code,
      vertragId: group?.id || "",
      ort: form.ort.trim() || "Basel",
      datum: form.datum,
      zaNr: form.zaNr.trim(),
      kuerzel: form.kuerzel.trim(),
      adressat: form.adressat.trim(),
      auftraggeberin: form.auftraggeberin.trim(),
      projekt: form.projekt.trim(),
      objektgliederung: og,
      unternehmerId: unternehmer?.id || "",
      unternehmerName: unternehmer?.name || unternehmerLines[0] || "",
      unternehmer: unternehmerText.trim(),
      auftrag: form.auftrag.trim(),
      vertragNr: form.vertragNr.trim(),
      vertragssumme,
      bisherigeZahlungen: bisherige,
      bisherigeAkonto,
      betrag,
      istAkonto: form.istAkonto === "ja",
      mwstSatz: parseFloat(form.mwstSatz) || 0,
      rechnungNr: form.rechnungNr.trim(),
      buchungstext: form.buchungstext.trim(),
      rechnungsdatum: form.rechnungsdatum,
      faelligkeit: form.faelligkeit.trim(),
      unterschrift1: { name: form.unterschrift1Name.trim(), funktion: form.unterschrift1Funktion.trim() },
      unterschrift2: { name: form.unterschrift2Name.trim(), funktion: form.unterschrift2Funktion.trim() },
    };
  };

  const validate = () => {
    if (mustChooseVertrag && !group) return "Bitte den Vertrag wählen, auf den sich die Zahlungsanweisung bezieht.";
    if (!unternehmerText.trim()) return "Unternehmer ist erforderlich (aus dem CRM wählen oder Adresse eintragen).";
    if (!(betrag > 0)) return "Betrag der heutigen Zahlung muss grösser als 0 sein.";
    if (!form.datum) return "Datum der Zahlungsanweisung ist erforderlich.";
    if (!form.zaNr.trim()) return "Nummer der Zahlungsanweisung ist erforderlich.";
    return "";
  };

  const paymentPayload = (zaDaten) => ({
    bezeichnung: `Zahlungsanweisung Nr. ${zaDaten.zaNr}${zaDaten.buchungstext ? `: ${zaDaten.buchungstext}` : ""}`,
    betrag: zaDaten.betrag,
    datum: zaDaten.datum,
    istAkonto: zaDaten.istAkonto,
    unternehmerId: zaDaten.unternehmerId,
    unternehmerName: zaDaten.unternehmerName,
    zaNr: zaDaten.zaNr,
    rechnungNr: zaDaten.rechnungNr,
    vertragId: zaDaten.vertragId,
    zaDaten,
  });

  // Eingaben fuer das naechste Mal merken (Objektgliederung liegt seit
  // US-KOST-023 in der Sidebar, seit 2026-09-30 an der Phase, nicht hier).
  const rememberDefaults = (zaDaten) => {
    writeJson(DEFAULTS_KEY, {
      ort: zaDaten.ort,
      faelligkeit: zaDaten.faelligkeit,
      mwstSatz: zaDaten.mwstSatz,
      unterschrift1: zaDaten.unterschrift1,
      unterschrift2: zaDaten.unterschrift2,
    });
    writeJson(estimateKey(estimateId), {
      kuerzel: zaDaten.kuerzel,
      adressat: zaDaten.adressat,
    });
  };

  // Zahlung speichern bzw. aktualisieren (+ optional Unternehmer auf Vertraegen
  // nachtragen). Wirft bei Speicherfehler; Nachtrag-Fehler nur als Warnung.
  const persistPayment = async (zaDaten) => {
    if (isEdit) {
      await onUpdatePayment(code, existingPayment.id, { ...existingPayment, ...paymentPayload(zaDaten) });
    } else {
      await onSavePayment(code, paymentPayload(zaDaten));
    }
    if (saveOnContracts && unternehmer?.id && onUpdateContract) {
      const offen = relevantContracts.filter((c) => !c.unternehmerId);
      for (const c of offen) {
        try {
          await onUpdateContract(code, c.id, {
            ...c,
            unternehmerId: unternehmer.id,
            unternehmerName: unternehmer.name,
          });
        } catch (err) {
          setWarn(`Zahlung gespeichert, Unternehmer konnte nicht auf allen Verträgen nachgetragen werden: ${err?.message || ""}`);
        }
      }
    }
  };

  // Hauptweg (Erstellen): erst Zahlung speichern, dann PDF (ADR 0054).
  const handleSubmit = async () => {
    setError("");
    setWarn("");
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    const zaDaten = buildZaDaten();
    setBusy(true);
    try {
      await persistPayment(zaDaten);
    } catch (err) {
      setError(`Zahlung konnte nicht gespeichert werden: ${err?.message || "Unbekannter Fehler"}`);
      setBusy(false);
      return;
    }
    rememberDefaults(zaDaten);
    await persistAuftraggeberin();
    try {
      await downloadZahlungsanweisungPdf(zaDaten);
    } catch (err) {
      setWarn(
        `Zahlung gespeichert, PDF konnte nicht erzeugt werden: ${err?.message || "Unbekannter Fehler"}. ` +
          "Der Download lässt sich über „PDF erneut herunterladen“ wiederholen."
      );
    }
    setDone({ zaDaten, saved: true, updated: false, pdf: true });
    setBusy(false);
  };

  // Bearbeiten: Zahlung aktualisieren, PDF auf Wunsch neu erzeugen (ADR 0064).
  const handleEditSave = async (withPdf) => {
    setError("");
    setWarn("");
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    const zaDaten = buildZaDaten();
    setBusy(true);
    try {
      await persistPayment(zaDaten);
    } catch (err) {
      setError(`Zahlung konnte nicht gespeichert werden: ${err?.message || "Unbekannter Fehler"}`);
      setBusy(false);
      return;
    }
    rememberDefaults(zaDaten);
    await persistAuftraggeberin();
    if (withPdf) {
      try {
        await downloadZahlungsanweisungPdf(zaDaten);
      } catch (err) {
        setWarn(
          `Änderungen gespeichert, PDF konnte nicht erzeugt werden: ${err?.message || "Unbekannter Fehler"}. ` +
            "Der Download lässt sich über „PDF erneut herunterladen“ wiederholen."
        );
      }
    }
    setDone({ zaDaten, saved: true, updated: true, pdf: withPdf });
    setBusy(false);
  };

  // "Nur PDF erzeugen": kein Eintrag in der Kostenkontrolle (z.B. Entwurf zur
  // Prüfung). Die Zahlung kann im Abschluss-Schritt noch eingetragen werden.
  const handlePdfOnly = async () => {
    setError("");
    setWarn("");
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    const zaDaten = buildZaDaten();
    setBusy(true);
    try {
      await downloadZahlungsanweisungPdf(zaDaten);
      rememberDefaults(zaDaten);
      await persistAuftraggeberin();
      setDone({ zaDaten, saved: false, updated: false, pdf: true });
    } catch (err) {
      setError(`PDF konnte nicht erzeugt werden: ${err?.message || "Unbekannter Fehler"}`);
    } finally {
      setBusy(false);
    }
  };

  // Aus dem Abschluss-Schritt nach "Nur PDF": Zahlung doch eintragen.
  const saveAfterPdfOnly = async () => {
    if (!done || done.saved) return;
    setError("");
    setBusy(true);
    try {
      await persistPayment(done.zaDaten);
      setDone({ ...done, saved: true });
    } catch (err) {
      setError(`Zahlung konnte nicht gespeichert werden: ${err?.message || "Unbekannter Fehler"}`);
    } finally {
      setBusy(false);
    }
  };

  const retryPdf = async () => {
    if (!done) return;
    setError("");
    try {
      await downloadZahlungsanweisungPdf(done.zaDaten);
      setWarn("");
    } catch (err) {
      setError(`PDF konnte nicht erzeugt werden: ${err?.message || "Unbekannter Fehler"}`);
    }
  };

  const money = (v) => `CHF ${formatSwissCurrency(v)}`;
  const title = isEdit
    ? `Zahlungsanweisung Nr. ${zd?.zaNr || existingPayment.zaNr || ""} bearbeiten · ${code} ${nodeName}`
    : `Zahlungsanweisung erstellen · ${code} ${nodeName}`;

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="modal-content estimate-modal za-modal">
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose} disabled={busy}>
            &times;
          </button>
        </div>

        {done ? (
          <>
            <div className="modal-body">
              {error && <div className="error-message">{error}</div>}
              {warn && <div className="za-warn">{warn}</div>}
              {done.updated ? (
                <>
                  <p className="za-done-text">
                    Zahlungsanweisung Nr. {done.zaDaten.zaNr} über {money(done.zaDaten.betrag)} wurde aktualisiert
                    (Zahlung auf Position {code}).{" "}
                    {done.pdf ? (
                      <>
                        Neues PDF: <code>{zahlungsanweisungDateiname(done.zaDaten)}</code>
                      </>
                    ) : (
                      "Das PDF wurde nicht neu erzeugt."
                    )}
                  </p>
                  <p className="za-done-hint">
                    Der Nachdruck über „PDF" im Detail-Panel verwendet ab jetzt die geänderten Angaben.
                  </p>
                </>
              ) : done.saved ? (
                <>
                  <p className="za-done-text">
                    Zahlungsanweisung Nr. {done.zaDaten.zaNr} über {money(done.zaDaten.betrag)} wurde als Zahlung auf
                    Position {code} eingetragen. Datei: <code>{zahlungsanweisungDateiname(done.zaDaten)}</code>
                  </p>
                  <p className="za-done-hint">
                    Die Zahlungsanweisung lässt sich im Detail-Panel der Position über „Bearbeiten“ anpassen (inkl.
                    neuem PDF) oder löschen. Das PDF kann dort über „PDF" erneut heruntergeladen werden.
                  </p>
                </>
              ) : (
                <>
                  <p className="za-done-text">
                    PDF <code>{zahlungsanweisungDateiname(done.zaDaten)}</code> wurde erzeugt. Es wurde{" "}
                    <strong>keine Zahlung</strong> in der Kostenkontrolle eingetragen.
                  </p>
                  <p className="za-done-hint">
                    Soll die Zahlung über {money(done.zaDaten.betrag)} doch gebucht werden, kann sie hier mit denselben
                    Angaben eingetragen werden.
                  </p>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={retryPdf} disabled={busy}>
                {done.pdf ? "PDF erneut herunterladen" : "PDF herunterladen"}
              </button>
              {!done.saved && (
                <button className="btn btn-secondary" onClick={saveAfterPdfOnly} disabled={busy}>
                  {busy ? "Wird eingetragen..." : "Zahlung jetzt eintragen"}
                </button>
              )}
              <button className="btn btn-primary" onClick={onClose} disabled={busy}>
                Schliessen
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="modal-body za-body">
              {error && <div className="error-message">{error}</div>}
              {warn && <div className="za-warn">{warn}</div>}

              {/* Automatisch uebernommene Angaben */}
              <div className="za-section-title">Automatisch übernommen (anpassbar)</div>
              <div className="za-grid">
                {groups.length > 0 && (
                  <div className="form-group za-span-2">
                    <label>Vertrag{mustChooseVertrag ? " *" : ""}</label>
                    {mustChooseVertrag ? (
                      <select value={vertragId} onChange={(e) => chooseVertrag(e.target.value)} disabled={busy}>
                        <option value="">Bitte wählen…</option>
                        {groups.map((g) => (
                          <option key={g.id} value={g.id}>
                            {groupLabel(g)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="za-readonly">{groupLabel(groups[0])}</div>
                    )}
                    {mustChooseVertrag && (
                      <div className="za-field-hint">
                        Die Position hat mehrere Verträge. Vertragssumme, bisherige Zahlungen, Unternehmer und Vertrag
                        Nr. beziehen sich auf den gewählten Vertrag samt seinen Nachträgen.
                      </div>
                    )}
                    {unassignedNachtraege.length > 0 && (
                      <div className="za-field-hint za-hint-warn">
                        {unassignedNachtraege.length} Nachtrag/Nachträge dieser Position sind keinem Vertrag
                        zugeordnet und fehlen in jeder Vertragssumme. Zuordnung über „Bearbeiten“ im Detail-Panel.
                      </div>
                    )}
                    {unassignedPayments.length > 0 && (
                      <div className="za-field-hint za-hint-warn">
                        {unassignedPayments.length} bisherige Zahlung(en) sind keinem Vertrag zugeordnet und fehlen
                        in „Bisherige Zahlungen“. Zuordnung über „Bearbeiten“ im Detail-Panel.
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group za-span-2">
                  <label>Unternehmer</label>
                  <div className="za-unternehmer-row">
                    {groups.length === 0 && unternehmerOptions.length > 1 && (
                      <select
                        value={unternehmer?.id || ""}
                        onChange={(e) =>
                          chooseUnternehmer(unternehmerOptions.find((u) => u.id === e.target.value) || null)
                        }
                        title="Unternehmer aus den Verträgen dieser Position"
                      >
                        {unternehmerOptions.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                      </select>
                    )}
                    <UnternehmerPicker
                      value={unternehmer}
                      onChange={(p) => chooseUnternehmer(p ? { id: p.id, name: p.name } : null)}
                      partners={partners}
                      onPartnersReload={onPartnersReload}
                      projectCode={linkedProject?.code || ""}
                      disabled={busy}
                    />
                  </div>
                  <textarea
                    rows={3}
                    value={unternehmerText}
                    onChange={(e) => setUnternehmerText(e.target.value)}
                    placeholder={"Firma\nStrasse\nPLZ Ort"}
                  />
                  {unternehmer?.id && relevantContracts.some((c) => !c.unternehmerId) && (
                    <label className="za-check">
                      <input
                        type="checkbox"
                        checked={saveOnContracts}
                        onChange={(e) => setSaveOnContracts(e.target.checked)}
                      />
                      Unternehmer auf den Verträgen {group ? "dieses Vertrags" : "dieser Position"} speichern
                    </label>
                  )}
                  {(group ? !groupUnternehmer(group) : unternehmerOptions.length === 0) && (
                    <div className="za-field-hint">
                      {group
                        ? "Kein Unternehmer an diesem Vertrag hinterlegt."
                        : "Kein Unternehmer an den Verträgen hinterlegt."}{" "}
                      Tipp: Unternehmer künftig direkt bei „+ Vertrag / Nachtrag" oder „+ Unternehmen" erfassen.
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Projekt</label>
                  <input type="text" value={form.projekt} onChange={(e) => set("projekt", e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Datum</label>
                  <input type="date" value={form.datum} onChange={(e) => set("datum", e.target.value)} />
                </div>

                <div className="form-group">
                  <label>Auftraggeberin{agLoading ? " (wird geladen…)" : ""}</label>
                  <div className="za-unternehmer-row">
                    <UnternehmerPicker
                      value={agPartner}
                      onChange={handleAgPick}
                      partners={partners}
                      onPartnersReload={onPartnersReload}
                      projectCode={linkedProject?.code || ""}
                      relationType={RELATION_TYPES.AUFTRAGGEBERIN}
                      createLabel="als neue Auftraggeberin im CRM erfassen"
                      placeholder="Auftraggeberin im CRM suchen oder neu erfassen…"
                      disabled={busy}
                    />
                  </div>
                  <textarea
                    rows={3}
                    value={form.auftraggeberin}
                    onChange={(e) => set("auftraggeberin", e.target.value)}
                    placeholder={"Auftraggeberin\nStrasse\nPLZ Ort"}
                  />
                  {agNeedsPersist && (
                    <label className="za-check">
                      <input type="checkbox" checked={agPersist} onChange={(e) => setAgPersist(e.target.checked)} />
                      {agAlreadyLinked
                        ? "Bisherige Auftraggeberin im Projekt ersetzen"
                        : "Im Projekt als Auftraggeberin hinterlegen"}
                      {agOtherNames.length > 0 && ` (entfernt: ${agOtherNames.join(", ")})`}
                    </label>
                  )}
                  {agStringOnly && !agPartner && (
                    <div className="za-field-hint">
                      Bisher nur als Text im Projekt hinterlegt. Über die Suche als CRM-Eintrag setzen, damit die Adresse
                      künftig automatisch übernommen wird.
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Adressat (Briefkopf links)</label>
                  <div className="za-unternehmer-row">
                    <UnternehmerPicker
                      value={adressatEntity}
                      onChange={handleAdressatPick}
                      partners={partners}
                      persons={persons}
                      onPartnersReload={async () => {
                        await onPartnersReload?.();
                        await reloadPersons();
                      }}
                      projectCode={linkedProject?.code || ""}
                      relationType={RELATION_TYPES.PROJECT_PARTNER}
                      createLabel="als neue Firma im CRM erfassen"
                      personCreateLabel="als neue Person im CRM erfassen"
                      placeholder="Firma oder Ansprechperson im CRM suchen…"
                      disabled={busy}
                    />
                  </div>
                  <textarea
                    rows={4}
                    value={form.adressat}
                    onChange={(e) => set("adressat", e.target.value)}
                    placeholder={"Firma\nAnsprechperson\nStrasse\nPLZ Ort"}
                  />
                  {!adressatEntity && (
                    <div className="za-field-hint">Standard: Auftraggeberin. Ansprechperson bei Bedarf ergänzen oder aus dem CRM wählen.</div>
                  )}
                </div>

                <div className="form-group">
                  <label>Auftrag</label>
                  <input type="text" value={form.auftrag} onChange={(e) => set("auftrag", e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Vertrag Nr.</label>
                  <input
                    type="text"
                    value={form.vertragNr}
                    onChange={(e) => set("vertragNr", e.target.value)}
                    placeholder={group ? "aus dem gewählten Vertrag" : "aus den Verträgen der Position"}
                  />
                </div>

                <div className="form-group">
                  <label>Vertragssumme total (inkl. MWST)</label>
                  <div
                    className="za-readonly"
                    title={
                      group
                        ? `Vertrag + ${group.nachtraege.length} Nachtrag/Nachträge`
                        : `${relevantContracts.length} Vertrag/Nachtrag`
                    }
                  >
                    {mustChooseVertrag && !group ? "Vertrag wählen" : money(vertragssumme)}
                  </div>
                  {frozen && (
                    <div className="za-field-hint">
                      Stand der ursprünglichen Zahlungsanweisung.
                      {frozenDiffers && (
                        <>
                          {" "}
                          Aktuell: {money(vertragssummeCalc)} / {money(bisherigeCalc)}.{" "}
                          <button type="button" className="za-link" onClick={() => setFrozen(null)} disabled={busy}>
                            Aus aktuellen Daten neu berechnen
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Bisherige Zahlungen</label>
                  <div
                    className="za-readonly"
                    title={`${relevantPayments.length} Zahlung(en)${isEdit ? " vor dieser Anweisung" : ""}`}
                  >
                    {mustChooseVertrag && !group ? (
                      "Vertrag wählen"
                    ) : (
                      <>
                        {money(bisherige)} <span className="za-pct">{pctOf(bisherige)}</span>
                      </>
                    )}
                  </div>
                  {isEdit && !frozen && (
                    <div className="za-field-hint">
                      Neu berechnet: Zahlungen dieser Vertragsgruppe vor dieser Anweisung (kleinere Nr. bzw. früheres
                      Datum).
                    </div>
                  )}
                </div>
              </div>

              {/* Angaben zur Zahlung */}
              <div className="za-section-title">Angaben zur Zahlung</div>
              <div className="za-grid">
                <div className="form-group">
                  <label>Zahlungsanweisung Nr. *</label>
                  <input type="text" value={form.zaNr} onChange={(e) => set("zaNr", e.target.value)} />
                  {zaNrVergeben(form.zaNr) ? (
                    <div className="za-field-hint za-hint-warn">
                      Nr. {form.zaNr.trim()} ist in dieser Phase bereits vergeben. Nächste freie Nummer:{" "}
                      {naechsteZaNr}.
                    </div>
                  ) : (
                    <div className="za-field-hint">Fortlaufend über alle Positionen dieser Phase.</div>
                  )}
                </div>
                <div className="form-group">
                  <label>Kürzel (z.B. GP)</label>
                  <input
                    type="text"
                    value={form.kuerzel}
                    onChange={(e) => set("kuerzel", e.target.value)}
                    placeholder="erscheint im Titel und Dateinamen"
                  />
                </div>
                <div className="form-group za-span-2">
                  <label>Objektgliederung</label>
                  <div className="za-readonly">{og || <span className="za-muted">Keine Objektgliederung hinterlegt</span>}</div>
                  <div className="za-field-hint">
                    Gilt für alle Zahlungsanweisungen des Dossiers und wird in der Seitenleiste unter „Zahlungsanweisung“
                    gepflegt.
                  </div>
                </div>

                <div className="form-group">
                  <label>Rechnungs-Nr.</label>
                  <input type="text" value={form.rechnungNr} onChange={(e) => set("rechnungNr", e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Datum Rechnung</label>
                  <input
                    type="date"
                    value={form.rechnungsdatum}
                    onChange={(e) => set("rechnungsdatum", e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Buchungstext</label>
                  <input
                    type="text"
                    value={form.buchungstext}
                    onChange={(e) => set("buchungstext", e.target.value)}
                    placeholder="z.B. Akontorechnung Nr. 001"
                  />
                </div>
                <div className="form-group">
                  <label>Datum Fälligkeit</label>
                  <input
                    type="text"
                    value={form.faelligkeit}
                    onChange={(e) => set("faelligkeit", e.target.value)}
                    placeholder="z.B. 30 Tage, netto"
                  />
                </div>

                <div className="form-group">
                  <label>Art</label>
                  <select value={form.istAkonto} onChange={(e) => set("istAkonto", e.target.value)}>
                    <option value="ja">Akonto</option>
                    <option value="nein">Schlusszahlung</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Heutige Zahlung (CHF, inkl. MWST) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.betrag}
                    onChange={(e) => set("betrag", e.target.value)}
                    autoFocus={!mustChooseVertrag || !!group}
                  />
                </div>
                <div className="form-group">
                  <label>MWST-Satz %</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={form.mwstSatz}
                    onChange={(e) => set("mwstSatz", e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Aufschlüsselung</label>
                  <div className="za-readonly za-breakdown">
                    <span>Netto {money(mwst.netto)}</span>
                    <span>
                      MWST {String(mwst.satz).replace(".", ",")} % {money(mwst.mwst)}
                    </span>
                    <span>
                      Total Zahlungen nachher {money(totalNachher)} <span className="za-pct">{pctOf(totalNachher)}</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Unterschriften */}
              <div className="za-section-title">Unterschriften</div>
              <div className="za-grid">
                <div className="form-group">
                  <label>Unterschrift 1 – Name</label>
                  <input
                    type="text"
                    value={form.unterschrift1Name}
                    onChange={(e) => set("unterschrift1Name", e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Unterschrift 1 – Funktion</label>
                  <input
                    type="text"
                    value={form.unterschrift1Funktion}
                    onChange={(e) => set("unterschrift1Funktion", e.target.value)}
                    placeholder="z.B. Generalplaner:in"
                  />
                </div>
                <div className="form-group">
                  <label>Unterschrift 2 – Name</label>
                  <input
                    type="text"
                    value={form.unterschrift2Name}
                    onChange={(e) => set("unterschrift2Name", e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Unterschrift 2 – Funktion</label>
                  <input
                    type="text"
                    value={form.unterschrift2Funktion}
                    onChange={(e) => set("unterschrift2Funktion", e.target.value)}
                    placeholder="z.B. Bauleiter:in"
                  />
                </div>
                <div className="form-group">
                  <label>Ort</label>
                  <input type="text" value={form.ort} onChange={(e) => set("ort", e.target.value)} />
                </div>
              </div>
            </div>

            <div className="modal-footer za-footer">
              <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
                Abbrechen
              </button>
              {isEdit ? (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleEditSave(false)}
                    disabled={busy}
                    title="Angaben speichern, ohne ein neues PDF zu erzeugen"
                  >
                    {busy ? "Wird gespeichert..." : "Speichern ohne PDF"}
                  </button>
                  <button className="btn btn-primary" onClick={() => handleEditSave(true)} disabled={busy}>
                    {busy ? "Wird gespeichert..." : "Speichern und PDF erzeugen"}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={handlePdfOnly}
                    disabled={busy}
                    title="Nur das Deckblatt herunterladen, ohne Zahlung in der Kostenkontrolle"
                  >
                    Nur PDF erzeugen
                  </button>
                  <button className="btn btn-primary" onClick={handleSubmit} disabled={busy}>
                    {busy ? "Wird erstellt..." : "PDF erzeugen und Zahlung eintragen"}
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ZahlungsanweisungModal;
